import { useCallback, useState } from "react";
import { createUseStyles } from "$utils/createUseStyles";
import clsx from "clsx";

import { CircularProgress } from "@mui/material";
import useJStyles from "$hooks/useJStyles";

import { Button, Popin, Text, Modal } from "$components";
import { Parcel, ParcelPopinMode, PreparationOrderStatus } from "$types";
import ParcelForm from "./ParcelForm";
import ParcelTable from "./ParcelTable";
import { pageConfig } from "$utils/typeConstants/Parcel";

type Props = {
  onCancel: () => void;
  parcel: Parcel;
  isFetchingParcel?: boolean;
  createParcel: (data: Parcel) => any;
  updateParcel: (data: Parcel) => any;
  deleteParcel: () => any;
  subTitle?: string;
  opStatus: PreparationOrderStatus;
  isOpen: boolean;
  onClose: () => void;
};

function ParcelPopin({
  onCancel,
  isOpen,
  parcel,
  isFetchingParcel = false,
  createParcel,
  updateParcel,
  deleteParcel,
  onClose,
  opStatus,
  subTitle = "",
}: Props) {
  const { jclasses } = useJStyles();
  const classes = useStyles();
  const [isDeleteModalOpen, toggleDeleteModal] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  let editMode: ParcelPopinMode =
    opStatus === PreparationOrderStatus.PREPARED ||
    opStatus === PreparationOrderStatus.IN_PROGRESS
      ? ParcelPopinMode.CREATE
      : ParcelPopinMode.READ;
  if (parcel?.packages?.length > 0 && editMode === ParcelPopinMode.CREATE) {
    editMode = ParcelPopinMode.EDIT;
  }

  const handleSubmit = useCallback(
    async (data) => {
      setError("");
      try {
        if (editMode === ParcelPopinMode.CREATE) {
          createParcel(data);
        } else {
          updateParcel(data);
        }
      } catch {
        setError("Une erreur est survenue");
      }
      onClose();
    },
    [editMode, createParcel, updateParcel, onClose]
  );

  const handleParcelDelete = useCallback(async () => {
    setError("");
    try {
      deleteParcel();
    } catch {
      setError("Une erreur est survenue");
    }
    toggleDeleteModal(false);
    onClose();
  }, [deleteParcel, onClose]);

  return (
    <Popin
      openPopin={isOpen}
      toggleDrawer={onClose}
      containerClassName={classes.popinContainer}
    >
      {isFetchingParcel ? (
        <div className={classes.spinnerContainer}>
          <CircularProgress />
        </div>
      ) : (
        <div className={clsx(jclasses.jWrapper, classes.wrapper)}>
          <div>
            <div className={classes.header}>
              <div>
                <Text type="h2">{pageConfig[editMode].title}</Text>
                <Text type="h3">{`Référence de l'OP : ${subTitle}`}</Text>
              </div>

              {pageConfig[editMode].displayDeleteButton ? (
                <Button
                  color="secondary"
                  className={classes.deleteButton}
                  onClick={() => toggleDeleteModal(true)}
                >
                  Supprimer le colisage
                </Button>
              ) : null}
            </div>

            {editMode === ParcelPopinMode.READ ? (
              <ParcelTable parcel={parcel} showDetailsTable />
            ) : (
              <ParcelForm parcel={parcel} onSubmit={handleSubmit} />
            )}
          </div>
          {error && <div className={classes.errorText}>{error}</div>}
          <div className={jclasses.footerForm}>
            <Button color="secondary" onClick={onCancel}>
              {pageConfig[editMode].cancelButtonText}
            </Button>
            {pageConfig[editMode].displaySubmitButton && (
              <Button type="submit" form="parcelForm">
                Enregistrer
              </Button>
            )}
          </div>
        </div>
      )}

      <Modal
        textValidate="Supprimer"
        textCancel="Annuler"
        open={isDeleteModalOpen}
        onValidate={handleParcelDelete}
        onCancel={() => toggleDeleteModal(false)}
      >
        <div className={jclasses.dialogContent} style={{ textAlign: "center" }}>
          <Text>
            Attention
            <br />
            Vous êtes sur le point de supprimer définitivement ce colisage.
            <br />
            Êtes-vous sûr de vouloir continuer ?
          </Text>
        </div>
      </Modal>
    </Popin>
  );
}

const useStyles = createUseStyles(() => ({
  wrapper: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    justifyContent: "space-between",
  },
  spinnerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
  },
  popinContainer: {
    display: "flex",
    flex: 1,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
  },
  deleteButton: {
    height: 40,
    alignSelf: "center",
  },
  errorText: {
    textAlign: "center",
    color: "red",
  },
}));

export default ParcelPopin;
